
import Vue from 'vue';
import type { Component } from 'vue';
import type { RawLocation } from 'vue-router';
import { mapActions, mapMutations, mapState } from 'vuex';
import { format, isToday, isYesterday } from 'date-fns';
import { getActiveStorageUrl } from '../WarRoomTextEditor/utils';
import EditIcon from './images/edit.vue.svg';
import ReplyIcon from './images/reply.vue.svg';
import WarRoomCommentCreateForm from '@/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateForm.vue';
import WarRoomCommentAuthor from '@/components/partials/war-room/WarRoomComment/WarRoomCommentAuthor.vue';
import MoreIcon from '@/assets/svg/more.vue.svg';
import WarRoomVote from '@/components/partials/war-room/WarRoomVote/WarRoomVote.vue';
import WarRoomShare from '@/components/partials/war-room/WarRoomShare/WarRoomShare.vue';
import { sharableTypes } from '@/components/partials/war-room/WarRoomShare/constants';
import { makeUrl } from '@/components/utils/browser/url';
import FlagIcon from '@/assets/svg/u_flag.vue.svg';
import TrashIcon from '@/assets/svg/u_trash-alt.vue.svg';
import Dropdown from '@/components/common/Dropdown/Dropdown.vue';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu.vue';
import DropdownToggler from '@/components/common/Dropdown/DropdownToggler.vue';
import DropdownItem from '@/components/common/Dropdown/DropdownItem.vue';
import { HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE } from '@/constants';
import WarRoomComments from '~/components/partials/war-room/WarRoomComments/WarRoomComments.vue';
import WarRoomCommentCreateFormEditor from '~/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateFormEditor.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import type { IPostComment, IPostUser } from '~/types/community';
import { isExtraRoleAdmin, dropRedundantWhitespace, fixLinkWhitespace } from '~/components/partials/war-room/utils';
import type { Cloudinary } from '~/nuxtconfig/image/types';
import { LAST_NESTING_LEVEL } from '~/components/partials/war-room/constants';

type PostCommentResponse = {
  comment_dict: Record<number, IPostComment>;
  user_dict: Record<number, IPostUser>;
  public_user_dict: Record<number, IPostUser>;
};

interface IData {
  isCreateCommentInputVisible: boolean;
  upserting: boolean;
  isEditMode: boolean;
  editCommentText: string;
  isHighlightedBackgroundVisible: boolean;
  sharableType: string;
  editedImageKey: string | null;
  editedImageMedia: string | null;
  imageUploading: boolean;
  imageOpenend: boolean;
  isVisible: boolean;
}

interface IAction {
  name: string;
  action: Function;
  visible: boolean;
  icon?: Component;
}

const BRAVOS_DOSE = 5;

export default Vue.extend({
  name: 'WarRoomComment',
  components: {
    WarRoomCommentCreateFormEditor,
    WarRoomCommentAuthor,
    ReplyIcon,
    EditIcon,
    WarRoomCommentCreateForm,
    WarRoomVote,
    WarRoomComments,
    BravadoButtonV3,
    WarRoomShare,
    FlagIcon,
    MoreIcon,
    Dropdown,
    DropdownMenu,
    DropdownToggler,
    DropdownItem,
    TrashIcon,
  },
  props: {
    comment: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      required: true,
    },
    post: {
      type: Object,
      default: () => ({}),
    },
    boostUsernames: {
      type: Array,
      default: () => [],
    },
    nestingLevel: {
      type: Number,
      default: 1,
    },
    parentId: {
      type: Number,
      default: null,
    },
  },
  data(): IData {
    return {
      isCreateCommentInputVisible: false,
      upserting: false,
      isEditMode: false,
      editCommentText: '',
      isHighlightedBackgroundVisible: false,
      sharableType: sharableTypes.COMMENT,
      editedImageKey: null,
      editedImageMedia: null,
      imageUploading: false,
      imageOpenend: false,
      isVisible: false,
    };
  },
  computed: {
    ...mapState(['settings']),
    ...mapState('warRoom', ['user_dict', 'public_user_dict']),
    actions(): IAction[] {
      return [
        {
          name: 'Hide',
          action: this.handleHide,
          visible: this.hasExtraActions,
        },
        {
          name: 'Mass Downvote',
          action: this.handleRating,
          visible: this.hasExtraActions,
        },
        {
          name: 'Reply',
          action: this.toggleCreateCommentInput,
          visible: true,
          icon: ReplyIcon,
        },
        {
          name: 'Edit',
          action: this.enterEditMode,
          visible: this.isAuthor,
          icon: EditIcon,
        },
        {
          name: 'Remove',
          action: this.removeCommentClick,
          visible: this.isAuthor || this.hasExtraActions,
          icon: TrashIcon,
        },
        {
          name: 'Flag an issue',
          action: this.openFlagIssueModal,
          visible: !this.isAuthor,
          icon: FlagIcon,
        },
      ].filter(a => a.visible);
    },
    author(): IPostUser {
      const postComment = this.comment as IPostComment;
      return this.user_dict[postComment.user_username] || this.public_user_dict[postComment.user_slug];
    },
    hasExtraActions(): boolean {
      return Boolean(this.$auth.user?.admin) && isExtraRoleAdmin();
    },
    isAuthor(): boolean {
      return (
        this.$auth?.loggedIn &&
        (this.$auth?.user.username === this.comment.user_username || this.$auth?.user.slug === this.comment.user_slug)
      );
    },
    isLastNestingLevel(): boolean {
      return LAST_NESTING_LEVEL === this.nestingLevel;
    },
    commentDate(): string {
      const comment: IPostComment = this.comment;
      const dateFieldToUse = comment.is_updated ? comment.updated_at! : comment.created_at;
      const commentDate = new Date(dateFieldToUse);

      let datePart;
      if (isToday(commentDate)) {
        datePart = 'Today';
      } else if (isYesterday(commentDate)) {
        datePart = 'Yesterday';
      } else {
        datePart = format(commentDate, 'MMM, dd');
      }

      return `${datePart}, ${format(commentDate, 'hh:mm aaa')}`;
    },
    isCommentTextEmpty(): boolean {
      return !this.editCommentText.replace(/&nbsp;|<br>/g, '').trim().length;
    },
    isSaveEditDisabled(): boolean {
      return this.isCommentTextEmpty || this.imageUploading;
    },
    isHighlighted(): boolean {
      return !!this.$route.params.commentId && Number(this.$route.params.commentId) === this.comment.id;
    },
    sharableUrl(): string {
      const route: RawLocation = {
        name: 'war-room-posts-postSlug-comments-commentId',
        params: { postSlug: this.post.slug, commentId: this.comment.id },
      };

      const { href } = this.$router.resolve(route);

      return makeUrl(href);
    },
    isUnreadIconVisible(): boolean {
      if (!this.$auth.loggedIn) return false;
      if (!this.post.viewed) return false;

      return !this.comment.viewed;
    },
    imageUrl(): string | null {
      return this.comment.image_key && getActiveStorageUrl(this.comment.image_key);
    },
    expandedImgModifiers(): Partial<Cloudinary.Modifiers> {
      return { ...(process.client && { h: (window.innerHeight * 0.9).toFixed() }) };
    },
  },
  watch: {
    '$route.params.commentId': {
      handler() {
        this.setUpCommentInersectionObserver();
      },
    },
  },
  beforeCreate() {
    this.$options.components!.WarRoomComments =
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require('@/components/partials/war-room/WarRoomComments/WarRoomComments.vue').default;
  },
  mounted() {
    this.setUpCommentInersectionObserver();

    const { specialUsers } = this.settings;
    const hideAfter = HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE.getTime();

    const createdAt = new Date(this.comment.created_at).getTime();
    const timeDiff = hideAfter - createdAt;

    if (
      specialUsers.includes(this.comment.user_username) &&
      !specialUsers.includes(this.$auth.user?.username) &&
      timeDiff < 0
    ) {
      this.hideCommentMutation({ post: this.post, commentId: this.comment.id });
    } else {
      this.isVisible = true;
    }
  },
  methods: {
    ...mapActions('warRoom', ['updateComment', 'hideComment', 'commentMassDownVote']),
    ...mapMutations('warRoom', ['addComment', 'addUsers']),
    ...mapMutations('warRoom', { hideCommentMutation: 'hideComment' }),
    toggleCreateCommentInput() {
      this.$analytics.logEvent('war-room__comment__reply__click');
      if (this.$auth.loggedIn) {
        this.isCreateCommentInputVisible = !this.isCreateCommentInputVisible;
      } else {
        this.$router.push(`?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`);
      }
    },
    async removeCommentClick() {
      this.$analytics.logEvent('war-room__comment__remove__click');
      await this.$router.replace({
        query: { ...this.$route.query, dynamic_modal: 'confirm-remove-comment', commentId: this.comment.id },
      });
    },
    // create reply
    async onPostComment({ text, media, is_anonymous }: { text: string; media: string; is_anonymous: boolean }) {
      this.upserting = true;
      const parentId = this.isLastNestingLevel ? this.parentId : this.comment.id;
      const insertAt = this.isLastNestingLevel ? this.index + 1 : 0;

      try {
        const {
          comment_dict: commentDict,
          user_dict: userDict,
          public_user_dict: publicUserDict,
        } = await this.$api.$post<PostCommentResponse>('/api/war_room/comments.json', {
          comment: {
            text,
            post_id: this.post.id,
            parent_id: parentId,
            is_anonymous,
            media,
          },
        });

        const createdComment = Object.values(commentDict)[0];

        this.$analytics.logEvent('war-room__1-post__comment__create', {
          comment_id: createdComment?.id,
          replyTo: 'comment',
        });
        // eslint-disable-next-line
        (this.$refs.commentCreateForm as any).clearEditor();
        this.isCreateCommentInputVisible = false;

        this.addUsers({ userDict, publicUserDict });
        Object.values(commentDict).forEach(comment => {
          this.addComment({ post: this.post, comment, parentId, insertAt });
        });
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      } finally {
        this.upserting = false;
      }
    },
    edit() {
      // eslint-disable-next-line
      (this.$refs.commentCreateForm as any).setText(this.comment.text);
      this.toggleCreateCommentInput();
    },
    async editComment() {
      if (this.imageUploading) {
        return;
      }

      try {
        // eslint-disable-next-line
        const newCommentField: any = {
          commentId: this.comment.id,
          text: dropRedundantWhitespace(this.editCommentText),
        };

        // if was uploaded new image we should check media
        if (this.editedImageMedia) {
          newCommentField.media = this.editedImageMedia;
          // if was deleted old image we should check key
        } else if (!this.editedImageKey) {
          newCommentField.media = null;
        }

        await this.updateComment(newCommentField);
      } catch (e) {
        this.$notice.error('Could not update comment.', e);
        this.$sentryUtils.error(e);
      }

      this.isEditMode = false;
      this.editCommentText = '';
    },
    enterEditMode() {
      this.$analytics.logEvent('war-room__comment__edit__click');

      this.editImageChange({ key: this.comment.image_key, media: null });

      this.isEditMode = true;
    },
    editImageRemove() {
      this.editImageChange({ key: null, media: null });
    },
    editImageChange({ key, media }: { key: string | null; media: string | null }) {
      this.editedImageMedia = media;
      this.editedImageKey = key;
    },
    editImageUploading(state: boolean) {
      this.imageUploading = state;
    },
    setUpCommentInersectionObserver() {
      if (!this.isHighlighted) return;

      this.isHighlightedBackgroundVisible = true;

      if ('IntersectionObserver' in window) {
        const intersectionObserver = new IntersectionObserver(entries => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            setTimeout(() => {
              this.isHighlightedBackgroundVisible = false;
              intersectionObserver.disconnect();
            }, 2000);
          }
        });
        intersectionObserver.observe(this.$el);
      } else {
        setTimeout(() => {
          this.isHighlightedBackgroundVisible = false;
        }, 2000);
      }
    },
    openFlagIssueModal(): void {
      this.$modal.showWithRoute(
        { custom: 'flag-issue' },
        { flaggableId: String(this.comment.id), flaggableType: 'WarRoom::Comment' },
      );
    },
    async handleHide() {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          dynamic_modal: 'confirm-hide-comment',
          commentId: this.comment.id,
          text: encodeURI(this.comment.text),
        },
      });
    },
    handleRating(): void {
      this.commentMassDownVote({ comment: this.comment, attributes: { downvotes_count: BRAVOS_DOSE } });
    },
    toggleExpandedImage() {
      if (!this.imageOpenend) {
        window.document.body.setAttribute('locked', 'true');
        this.imageOpenend = true;
      } else {
        this.hideExpandedImage();
      }
    },
    hideExpandedImage() {
      window.document.body.removeAttribute('locked');
      this.imageOpenend = false;
    },

    fixComment(text: string) {
      return fixLinkWhitespace(text);
    },
  },
});
